import {appQueries} from '@app/app-queries';
import {
  AccountSettingsId,
  AccountSettingsSidenavItem,
} from '@common/auth/ui/account-settings/account-settings-sidenav';
import {AccountSettingsPanel} from '@common/auth/ui/account-settings/account-settings-panel';
import {useQuery} from '@tanstack/react-query';
import {apiClient} from '@common/http/query-client';
import {Trans} from '@ui/i18n/trans';
import {message} from '@ui/i18n/message';
import {useTrans} from '@ui/i18n/use-trans';
import {Option} from '@ui/forms/combobox/combobox';
import {Select} from '@ui/forms/select/select';
import {Switch} from '@ui/forms/toggle/switch';
import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router';
import {useUser} from '@common/auth/ui/use-user';

export function AutoplaySettingsSidenavItem() {
  return (
    <AccountSettingsSidenavItem
      panel={AccountSettingsId.Autoplay}
      icon={<span className="text-xs">▶</span>}
    >
      <Trans message="Autoplay" />
    </AccountSettingsSidenavItem>
  );
}

export function AutoplaySettingsPanel() {
  const {trans} = useTrans();
  const {data: playlists = []} = useQuery(
    appQueries.playlists.compactAuthUserPlaylists(),
  );
  const {data: userData, refetch: refetchUser} = useUser('me', {
    with: ['autostartPlaylist'],
  });
  const navigate = useNavigate();
  const [selectedId, setSelectedId] = useState('');
  const [fadeOutOnPause, setFadeOutOnPause] = useState(false);
  const [crossFadeEnabled, setCrossFadeEnabled] = useState(false);

  useEffect(() => {
    setSelectedId(`${userData?.user.autostart_playlist_id || ''}`);
  }, [userData?.user.autostart_playlist_id]);
  useEffect(() => {
    setFadeOutOnPause(userData?.user.fade_out_on_pause ?? false);
  }, [userData?.user.fade_out_on_pause]);
  useEffect(() => {
    setCrossFadeEnabled(userData?.user.cross_fade_enabled ?? false);
  }, [userData?.user.cross_fade_enabled]);

  const updateUser = (values: Record<string, unknown>) =>
    apiClient.put('users/' + userData?.user.id, values).then(() => refetchUser());

  return (
    <AccountSettingsPanel
      id={AccountSettingsId.Autoplay}
      title={<Trans message="Autoplay" />}
    >
      <label className="mb-8 block text-sm font-medium">
        <Trans message="Autoplay playlist" />
      </label>
      <Select
        selectionMode="single"
        selectedValue={selectedId}
        onSelectionChange={value => {
          const id = `${value}`;
          setSelectedId(id);
          apiClient
            .put(`users/${userData?.user.id}`, {
              autostart_playlist_id: id ? Number(id) : null,
            })
            .then(() => {
              refetchUser();
              if (playlists.some(item => `${item.id}` === id)) navigate('/');
            });
        }}
        placeholder={trans(message('Select a playlist'))}
      >
        {playlists.map(playlist => (
          <Option key={playlist.id} value={`${playlist.id}`}>
            {playlist.name}
          </Option>
        ))}
      </Select>
      <p className="mt-8 text-sm text-muted">
        <Trans message="This playlist will replace the queue and start playing when autoplay is triggered." />
      </p>
      <Switch
        className="mt-24"
        checked={fadeOutOnPause}
        onChange={event => {
          const enabled = event.target.checked;
          setFadeOutOnPause(enabled);
          updateUser({fade_out_on_pause: enabled});
        }}
      >
        <Trans message="Fade out music when pausing" />
      </Switch>
      <Switch
        className="mt-16"
        checked={crossFadeEnabled}
        onChange={event => {
          const enabled = event.target.checked;
          setCrossFadeEnabled(enabled);
          updateUser({cross_fade_enabled: enabled});
        }}
      >
        <Trans message="Professional crossfade" />
      </Switch>
    </AccountSettingsPanel>
  );
}
